import React, { useState, useEffect, useRef } from 'react';
import { getProducts, getSettings, updateSettings } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { canEdit } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Loader2, 
  Tag,
  Save,
  Eye,
  QrCode,
  Barcode,
  Type,
  Calendar,
  Package,
  Hash,
  Thermometer
} from 'lucide-react';

const LABEL_FIELDS = [
  { id: 'product_name', label: 'Product Name', icon: Package, defaultEnabled: true },
  { id: 'product_sku', label: 'SKU/Code', icon: Hash, defaultEnabled: true },
  { id: 'batch_code', label: 'Batch Code', icon: Tag, defaultEnabled: true },
  { id: 'use_by_date', label: 'Use By Date', icon: Calendar, defaultEnabled: true },
  { id: 'production_date', label: 'Production Date', icon: Calendar, defaultEnabled: false },
  { id: 'weight', label: 'Weight', icon: Package, defaultEnabled: true },
  { id: 'storage', label: 'Storage Instructions', icon: Thermometer, defaultEnabled: true },
  { id: 'barcode', label: 'Barcode (EAN-13)', icon: Barcode, defaultEnabled: true },
  { id: 'qrcode', label: 'QR Code', icon: QrCode, defaultEnabled: false },
];

const LABEL_SIZES = [
  { id: '100x50', label: '100mm x 50mm (Standard)', width: 100, height: 50 },
  { id: '80x40', label: '80mm x 40mm (Small)', width: 80, height: 40 },
  { id: '150x100', label: '150mm x 100mm (Large)', width: 150, height: 100 },
];

export default function LabelDesigner() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [previewProduct, setPreviewProduct] = useState(null);
  
  // Template settings
  const [template, setTemplate] = useState({
    name: 'Default Label',
    size: '100x50',
    enabledFields: ['product_name', 'product_sku', 'batch_code', 'use_by_date', 'weight', 'storage', 'barcode'],
    fontSize: 'medium',
    showCompanyName: true,
    companyName: 'Cashel Blue'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [productsRes, settingsRes] = await Promise.allSettled([
        getProducts({ is_active: true }),
        getSettings()
      ]);
      
      const productsData = productsRes.status === 'fulfilled' ? productsRes.value.data || [] : [];
      const settingsData = settingsRes.status === 'fulfilled' ? settingsRes.value.data || {} : {};
      
      setProducts(productsData);
      
      // Load saved template if exists
      if (settingsData?.label_template) {
        setTemplate(prev => ({ ...prev, ...settingsData.label_template }));
      }
      if (settingsData?.company_name) {
        setTemplate(prev => ({ ...prev, companyName: settingsData.company_name }));
      }
      
      // Set default preview product
      if (productsData.length > 0) {
        setPreviewProduct(productsData[0]);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleField = (fieldId) => {
    setTemplate(prev => {
      const enabled = prev.enabledFields.includes(fieldId);
      return {
        ...prev,
        enabledFields: enabled 
          ? prev.enabledFields.filter(f => f !== fieldId)
          : [...prev.enabledFields, fieldId]
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings({ label_template: template });
      toast.success('Label template saved');
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const currentSize = LABEL_SIZES.find(s => s.id === template.size) || LABEL_SIZES[0];

  // Generate sample barcode (EAN-13 format visual)
  const generateBarcodePattern = () => {
    const pattern = [];
    for (let i = 0; i < 95; i++) {
      pattern.push(Math.random() > 0.5);
    }
    return pattern;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="label-designer-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Tag className="w-5 h-5" />
            Label Template Designer
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Design product labels with barcodes and QR codes
          </p>
        </div>
        {canEdit(user?.role) && (
          <Button onClick={handleSave} disabled={saving} data-testid="btn-save-template">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Template
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Settings Panel */}
        <div className="space-y-4">
          {/* Basic Settings */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Label Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Template Name</Label>
                <Input
                  value={template.name}
                  onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                  placeholder="e.g., Retail Label"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Label Size</Label>
                <Select value={template.size} onValueChange={(v) => setTemplate({ ...template, size: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LABEL_SIZES.map(size => (
                      <SelectItem key={size.id} value={size.id}>{size.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Font Size</Label>
                <Select value={template.fontSize} onValueChange={(v) => setTemplate({ ...template, fontSize: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="large">Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox 
                  id="showCompany"
                  checked={template.showCompanyName}
                  onCheckedChange={(checked) => setTemplate({ ...template, showCompanyName: checked })}
                />
                <label htmlFor="showCompany" className="text-sm">Show company name on label</label>
              </div>
            </CardContent>
          </Card>

          {/* Field Selection */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Fields to Display</CardTitle>
              <CardDescription className="text-xs">Select which information to show on the label</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {LABEL_FIELDS.map(field => {
                  const Icon = field.icon;
                  const enabled = template.enabledFields.includes(field.id);
                  return (
                    <div 
                      key={field.id}
                      className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                        enabled ? 'bg-primary/10 border-primary' : 'bg-muted/30 border-transparent'
                      }`}
                      onClick={() => toggleField(field.id)}
                    >
                      <Checkbox checked={enabled} />
                      <Icon className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{field.label}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Preview Product Selection */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Preview with Product</CardTitle>
            </CardHeader>
            <CardContent>
              <Select 
                value={previewProduct?.id || ''} 
                onValueChange={(v) => setPreviewProduct(products.find(p => p.id === v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select product..." />
                </SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </div>

        {/* Preview Panel */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Label Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center p-4 bg-muted/30 rounded-lg">
              {/* Label Preview */}
              <div 
                className="bg-white border-2 border-dashed border-zinc-300 rounded p-3 shadow-sm"
                style={{
                  width: `${currentSize.width * 2.5}px`,
                  minHeight: `${currentSize.height * 2.5}px`,
                  fontSize: template.fontSize === 'small' ? '10px' : template.fontSize === 'large' ? '14px' : '12px'
                }}
              >
                {/* Company Name */}
                {template.showCompanyName && (
                  <div className="text-center font-bold border-b pb-1 mb-2" style={{ fontSize: '1.2em' }}>
                    {template.companyName}
                  </div>
                )}

                {/* Product Name */}
                {template.enabledFields.includes('product_name') && (
                  <div className="font-bold mb-1">
                    {previewProduct?.name || 'Product Name'}
                  </div>
                )}

                {/* SKU */}
                {template.enabledFields.includes('product_sku') && (
                  <div className="text-muted-foreground font-mono mb-1">
                    SKU: {previewProduct?.sku || 'CB-001'}
                  </div>
                )}

                {/* Batch Code */}
                {template.enabledFields.includes('batch_code') && (
                  <div className="font-mono mb-1">
                    Batch: CB24010001
                  </div>
                )}

                {/* Dates */}
                <div className="flex gap-3 mb-1">
                  {template.enabledFields.includes('production_date') && (
                    <div className="text-xs">
                      <span className="text-muted-foreground">Prod: </span>01/01/2026
                    </div>
                  )}
                  {template.enabledFields.includes('use_by_date') && (
                    <div className="text-xs font-medium">
                      <span className="text-muted-foreground">Use By: </span>
                      <span className="text-red-600">15/03/2026</span>
                    </div>
                  )}
                </div>

                {/* Weight */}
                {template.enabledFields.includes('weight') && (
                  <div className="font-mono mb-1">
                    Net: {previewProduct?.weight_per_unit || '0.25'} kg
                  </div>
                )}

                {/* Storage */}
                {template.enabledFields.includes('storage') && (
                  <div className="text-xs text-muted-foreground mb-2">
                    {previewProduct?.storage_requirements || 'Keep refrigerated 2-8°C'}
                  </div>
                )}

                {/* Barcode */}
                {template.enabledFields.includes('barcode') && (
                  <div className="flex flex-col items-center mt-2 pt-2 border-t">
                    <div className="flex h-8 gap-px">
                      {generateBarcodePattern().map((bar, i) => (
                        <div 
                          key={i} 
                          className={bar ? 'bg-black' : 'bg-white'}
                          style={{ width: '1px' }}
                        />
                      ))}
                    </div>
                    <div className="font-mono text-xs mt-1">5 391234 567890</div>
                  </div>
                )}

                {/* QR Code */}
                {template.enabledFields.includes('qrcode') && (
                  <div className="flex justify-center mt-2 pt-2 border-t">
                    <div className="w-16 h-16 bg-zinc-100 border flex items-center justify-center">
                      <QrCode className="w-12 h-12 text-zinc-400" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 text-center text-sm text-muted-foreground">
              Label size: {currentSize.width}mm × {currentSize.height}mm
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
